// Finance glossary: powers the hover tooltips on story summaries and the
// "Explain this" box on every story card. Hand-written, so it costs nothing
// to run, is instant, and never invents facts (unlike an AI summary).
//
// Each term: id, label, patterns (whole-word, case-insensitive), optional
// regex (raw regex sources, for words that need context), what (what it
// is, one sentence) and why (why it can matter for markets, one sentence).
// Wording is educational and neutral: no predictions, no advice.
// Translations live in src/i18n/<lang>.js under `glossary`; detection stays
// on these (mostly English) patterns because most headlines are in English.

import { t, glossaryText } from '../i18n/index.js';

export const TERMS = [
    // ---- Central banks & rates ----
    { id: 'central-bank', label: 'Central bank', patterns: ['central bank', 'central banks'],
      what: 'The institution that sets a country’s interest rates and manages its money supply.',
      why: 'Its decisions change the cost of borrowing for everyone, which moves bonds, stocks and the currency.' },
    { id: 'fed', label: 'Federal Reserve (Fed)', patterns: ['federal reserve', 'the fed', 'fomc', 'fed chair', 'powell'],
      what: 'The central bank of the United States; its committee (FOMC) sets US interest rates.',
      why: 'US rates influence borrowing costs and investment flows worldwide, so Fed news often moves global markets.' },
    { id: 'ecb', label: 'European Central Bank (ECB)', patterns: ['ecb', 'european central bank', 'lagarde', 'bce', 'ezb'],
      what: 'The central bank for the 20 countries that use the euro.',
      why: 'Its rate decisions affect mortgages, business loans and the euro’s value across the eurozone.' },
    { id: 'boe', label: 'Bank of England', patterns: ['bank of england', 'boe'],
      what: 'The UK’s central bank; its Monetary Policy Committee sets UK interest rates.',
      why: 'Its decisions move the pound, UK government bonds (gilts) and UK borrowing costs.' },
    { id: 'boj', label: 'Bank of Japan', patterns: ['bank of japan', 'boj'],
      what: 'Japan’s central bank.',
      why: 'Changes to Japan’s very low rates can shift the yen and global investment flows.' },
    { id: 'interest-rate', label: 'Interest rate', patterns: ['interest rate', 'interest rates', 'policy rate', 'benchmark rate'],
      what: 'The price of borrowing money, usually set as a yearly percentage.',
      why: 'Higher rates make loans more expensive and tend to cool spending; lower rates do the opposite.' },
    { id: 'rate-cut', label: 'Rate cut', patterns: ['rate cut', 'rate cuts', 'cuts rates', 'cut rates'],
      what: 'A central bank lowering its interest rate.',
      why: 'Cheaper borrowing can support spending and company profits, but can also signal worries about the economy.' },
    { id: 'rate-hike', label: 'Rate hike', patterns: ['rate hike', 'rate hikes', 'raises rates', 'raised rates'],
      what: 'A central bank raising its interest rate.',
      why: 'It usually aims to slow inflation, and makes borrowing more expensive for households and companies.' },
    { id: 'basis-points', label: 'Basis points', patterns: ['basis points', 'basis point', 'bps'],
      what: 'One basis point is 0.01%; 25 basis points is a quarter of a percentage point.',
      why: 'Rate changes are quoted in basis points, so it tells you how big a move is.' },
    { id: 'qe', label: 'Quantitative easing (QE)', patterns: ['quantitative easing', 'quantitative tightening'],
      what: 'A central bank buying (QE) or reducing (QT) large amounts of bonds to influence long-term rates.',
      why: 'It changes how much money flows through financial markets.' },

    // ---- Economy ----
    { id: 'inflation', label: 'Inflation', patterns: ['inflation', 'consumer prices', 'cpi', 'inflación', 'inflazione', 'inflação'],
      what: 'The rate at which prices rise over time; often measured by the consumer price index (CPI).',
      why: 'High inflation can push central banks to keep rates high; it also reduces what money can buy.' },
    { id: 'gdp', label: 'GDP', patterns: ['gdp', 'gross domestic product', 'pib', 'pil'],
      what: 'Gross domestic product: the total value of goods and services an economy produces.',
      why: 'It’s the broadest measure of whether an economy is growing or shrinking.' },
    { id: 'recession', label: 'Recession', patterns: ['recession', 'recessions'],
      what: 'A significant, broad decline in economic activity, often defined as two quarters of falling GDP.',
      why: 'Recessions usually mean weaker company profits and higher unemployment.' },
    { id: 'unemployment', label: 'Unemployment', patterns: ['unemployment', 'jobless', 'payrolls', 'jobs report'],
      what: 'The share of people who want a job but don’t have one; payrolls count jobs added.',
      why: 'Job data shows the economy’s strength and influences central-bank decisions.' },
    { id: 'pmi', label: 'PMI', patterns: ['pmi', 'purchasing managers'],
      what: 'Purchasing Managers’ Index: a monthly survey of business activity. Above 50 means expansion.',
      why: 'It’s one of the earliest signals of where the economy is heading.' },
    { id: 'trade-deficit', label: 'Trade balance', patterns: ['trade deficit', 'trade surplus', 'trade balance'],
      what: 'The difference between what a country exports and imports.',
      why: 'Large imbalances can affect a currency and lead to trade disputes.' },
    { id: 'tariff', label: 'Tariff', patterns: ['tariff', 'tariffs', 'aranceles', 'dazi', 'zölle'],
      what: 'A tax on imported goods.',
      why: 'Tariffs raise costs for importers and consumers and can hit exporters’ sales and profits.' },
    { id: 'sanctions', label: 'Sanctions', patterns: ['sanctions', 'sanctioned', 'embargo'],
      what: 'Restrictions one country places on trade or finance with another.',
      why: 'They can cut off markets, disrupt supply chains and move commodity prices.' },
    { id: 'budget', label: 'Budget & fiscal policy', patterns: ['fiscal', 'budget deficit', 'public debt', 'national debt', 'stimulus'],
      what: 'How a government taxes, spends and borrows.',
      why: 'Big deficits or stimulus can affect growth, bond yields and the currency.' },

    // ---- Markets ----
    { id: 'bond-yield', label: 'Bond yield', patterns: ['bond yield', 'bond yields', 'treasury yields', 'yields', 'gilts', 'treasuries'],
      what: 'The return an investor earns on a bond; when bond prices fall, yields rise.',
      why: 'Government bond yields set the baseline cost of borrowing across the economy.' },
    // 'nasdaq' is also the exchange operator: "Nasdaq Listing Rule 5635(c)(4)",
    // "(Nasdaq: ABCD)", "listed on Nasdaq" are not about the index.
    { id: 'index', label: 'Stock index', patterns: ['s&p 500', 'dow jones', 'nasdaq composite', 'nasdaq 100', 'nasdaq-100', 'ftse', 'dax', 'cac 40', 'nikkei', 'hang seng', 'stoxx', 'sensex', 'nifty'],
      regex: ['(?<!(?:listed|listing|trades?|traded|trading|quoted)\\s+(?:on\\s+)?(?:the\\s+)?)nasdaq(?![\\s-]*(?::|listing|listed|rules?(?![\\p{L}])|stock (?:market|exchange)|global|capital market|exchange|under|ticker|symbol))'],
      what: 'A basket of stocks that tracks a market, such as the S&P 500 (500 large US companies).',
      why: 'Indexes summarise how a whole market is doing in one number.' },
    { id: 'sell-off', label: 'Sell-off', patterns: ['sell-off', 'selloff', 'sell off'],
      what: 'A period when many investors sell at once, pushing prices down quickly.',
      why: 'It often reflects fear or new bad news; it says nothing certain about what happens next.' },
    { id: 'volatility', label: 'Volatility', patterns: ['volatility', 'volatile'],
      what: 'How much and how fast prices move up and down.',
      why: 'Higher volatility means more uncertainty and bigger swings, in both directions.' },
    { id: 'currency', label: 'Exchange rate', patterns: ['exchange rate', 'exchange rates', 'forex', 'the dollar', 'the euro', 'the yen', 'sterling'],
      what: 'The price of one currency in terms of another.',
      why: 'Currency moves change the cost of imports, travel and the value of foreign investments.' },
    { id: 'oil', label: 'Oil prices', patterns: ['oil price', 'oil prices', 'crude', 'brent', 'opec'],
      what: 'The market price of crude oil; Brent and WTI are the main benchmarks. OPEC is a group of oil-producing countries.',
      why: 'Oil feeds into fuel, transport and production costs, and so into inflation.' },
    { id: 'gold', label: 'Gold', patterns: ['gold price', 'gold prices', 'price of gold'],
      what: 'Gold traded as a commodity.',
      why: 'Many investors buy it in uncertain times, so its price often reflects market nerves.' },
    { id: 'crypto', label: 'Cryptocurrency', patterns: ['bitcoin', 'crypto', 'cryptocurrency', 'ether', 'ethereum'],
      what: 'Digital assets such as bitcoin, traded 24/7 without a central bank.',
      why: 'Prices are very volatile and react to regulation and risk appetite.' },
    { id: 'etf', label: 'ETF', patterns: ['etf', 'etfs', 'exchange-traded fund'],
      what: 'A fund that trades on an exchange like a stock and usually tracks an index.',
      why: 'ETFs are a common, low-cost way for people to invest in a whole market.' },

    // ---- Companies ----
    { id: 'earnings', label: 'Earnings', patterns: ['earnings', 'quarterly results', 'quarterly profit', 'net profit', 'eps'],
      what: 'A company’s reported profit for a period, often per share (EPS).',
      why: 'Results above or below what analysts expected often move the share price.' },
    { id: 'revenue', label: 'Revenue', patterns: ['revenue', 'revenues', 'sales'],
      what: 'The total money a company takes in from selling its products or services, before costs.',
      why: 'Revenue growth shows whether demand for a company’s products is rising.' },
    { id: 'guidance', label: 'Guidance', patterns: ['guidance', 'outlook', 'forecast'],
      what: 'A company’s own forecast for its future results.',
      why: 'Changes to guidance often matter to investors as much as the past results.' },
    // not "Nasdaq Listing Rule 5635" / "listing requirements" (exchange rules, not an IPO)
    { id: 'ipo', label: 'IPO', patterns: ['ipo', 'initial public offering', 'flotation'],
      regex: ['listing(?!\\s+(?:rules?|requirements?|standards?|compliance)(?![\\p{L}]))'],
      what: 'Initial public offering: when a company first sells shares on a stock exchange.',
      why: 'It lets the public invest in the company and shows appetite for new listings.' },
    { id: 'merger', label: 'Merger & acquisition', patterns: ['merger', 'mergers', 'acquisition', 'acquisitions', 'takeover', 'buyout'],
      what: 'One company combining with or buying another.',
      why: 'Deals can change a market’s competition and often move both companies’ share prices.' },
    { id: 'buyback', label: 'Share buyback', patterns: ['buyback', 'share buyback', 'repurchase'],
      what: 'A company buying back its own shares from the market.',
      why: 'It returns money to shareholders and reduces the number of shares; it is not a guarantee of value.' },
    { id: 'dividend', label: 'Dividend', patterns: ['dividend', 'dividends'],
      what: 'A share of a company’s profit paid out to shareholders.',
      why: 'Dividend changes signal how confident a company is about its cash.' },
    { id: 'layoffs', label: 'Layoffs', patterns: ['layoffs', 'job cuts', 'redundancies'],
      what: 'A company reducing its number of employees.',
      why: 'It can signal cost pressure or restructuring, and affects the job market.' },
    { id: 'bankruptcy', label: 'Bankruptcy', patterns: ['bankruptcy', 'insolvency', 'chapter 11'],
      what: 'A legal process when a company can’t pay its debts.',
      why: 'Shareholders often lose most of their investment; lenders may recover part of theirs.' },

    // ---- Research (quant terms, mostly seen on the Research page) ----
    // Patterns are specific phrases: "gaining momentum" or "Alpha Cognition"
    // in a news headline must not trigger them.
    { id: 'sharpe', label: 'Sharpe ratio', patterns: ['sharpe ratio', 'sharpe ratios', 'risk-adjusted return', 'risk-adjusted returns'],
      what: 'Return earned per unit of risk taken: extra return over a safe rate, divided by volatility.',
      why: 'Two strategies with the same return are not equal: the one with the higher Sharpe ratio got there with less risk.' },
    { id: 'alpha', label: 'Alpha (excess return)', patterns: ['excess return', 'excess returns', 'abnormal return', 'abnormal returns', 'alpha generation', 'alpha signals', 'alpha factors'],
      what: 'Return above what the market or a benchmark explains: the part attributed to skill or a genuine edge.',
      why: 'Most claimed alpha disappears after costs or in new data, so it is the first thing to check in any strategy.' },
    { id: 'factor', label: 'Factor investing', patterns: ['factor model', 'factor models', 'factor investing', 'factor premia', 'factor premium', 'value factor', 'size factor', 'quality factor', 'low-volatility factor'],
      what: 'Explaining and choosing investments by shared traits (value, size, momentum, quality) instead of one stock at a time.',
      why: 'Many funds and ETFs are built on factors; research shows when they work and when they stop working.' },
    { id: 'momentum', label: 'Momentum strategy', patterns: ['momentum strategy', 'momentum strategies', 'momentum effect', 'momentum factor', 'time-series momentum', 'cross-sectional momentum', 'momentum returns'],
      what: 'Buying assets that have risen recently and selling those that have fallen, betting the trend continues.',
      why: 'Momentum is one of the most studied patterns in finance, but it can crash suddenly when trends reverse.' },
    { id: 'mean-reversion', label: 'Mean reversion', patterns: ['mean reversion', 'mean-reverting', 'mean reverting'],
      what: 'The tendency of a price or ratio to drift back towards its long-run average after moving away from it.',
      why: 'Many trading strategies bet on it, but a price that "should" revert can keep moving the other way for a long time.' },
    { id: 'backtest', label: 'Backtest', patterns: ['backtest', 'backtests', 'backtesting', 'backtested', 'out-of-sample', 'in-sample'],
      what: 'Testing a strategy on past data to see how it would have performed.',
      why: 'Good backtests can still fail live: results tuned to old data (overfitting) often do not repeat.' },
    { id: 'drawdown', label: 'Drawdown', patterns: ['drawdown', 'drawdowns', 'maximum drawdown', 'max drawdown'],
      what: 'The fall from a peak to the following low, usually in percent.',
      why: 'It shows the worst loss an investor would have lived through, which matters more to most people than average returns.' },
    { id: 'arbitrage', label: 'Arbitrage', patterns: ['arbitrage', 'statistical arbitrage', 'arbitrageurs'],
      what: 'Profiting from price differences for the same or similar assets, ideally with little risk.',
      why: 'Arbitrage keeps prices in line across markets; in practice it carries more risk than the textbook version.' },
    { id: 'order-book', label: 'Order book', patterns: ['order book', 'order books', 'limit order book', 'limit order books', 'market microstructure', 'order flow'],
      what: 'The live list of buy and sell orders waiting at each price on an exchange.',
      why: 'It shows supply and demand in real time and how much a large trade would move the price.' },
    { id: 'liquidity', label: 'Liquidity', patterns: ['market liquidity', 'liquidity risk', 'illiquid', 'illiquidity', 'bid-ask spread', 'bid-ask spreads'],
      what: 'How easily something can be bought or sold quickly without moving its price.',
      why: 'Illiquid assets can be hard to sell in a crisis, often at a big discount.' },
    { id: 'implied-vol', label: 'Implied volatility', patterns: ['implied volatility', 'implied volatilities', 'volatility surface', 'volatility surfaces', 'volatility smile', 'stochastic volatility'],
      what: 'The future price swings that option prices imply; the market’s own estimate of risk.',
      why: 'It rises when investors are nervous, which is why the VIX is often called the fear gauge.' },
    { id: 'options', label: 'Options', patterns: ['option pricing', 'options pricing', 'call options', 'put options', 'option prices', 'american options', 'european options'],
      what: 'Contracts giving the right, not the obligation, to buy (call) or sell (put) at a set price by a set date.',
      why: 'Used to hedge or to bet on price moves; they can lose their whole value quickly.' },
    { id: 'var', label: 'Value at risk (VaR)', patterns: ['value at risk', 'value-at-risk', 'expected shortfall', 'tail risk'],
      what: 'An estimate of how much a portfolio could lose on a bad day (for example, the worst 1 day in 100).',
      why: 'Banks and funds use it to set limits, but it says little about how bad the very worst days can be.' },
    { id: 'ml', label: 'Machine learning', patterns: ['machine learning', 'deep learning', 'neural network', 'neural networks', 'reinforcement learning', 'large language model', 'large language models'],
      what: 'Computer models that learn patterns from data instead of following hand-written rules.',
      why: 'Powerful at finding patterns, but in markets they easily find patterns that are pure chance.' },
    { id: 'portfolio-opt', label: 'Portfolio optimisation', patterns: ['portfolio optimization', 'portfolio optimisation', 'portfolio allocation', 'asset allocation', 'mean-variance', 'efficient frontier'],
      what: 'Choosing how much to put in each investment to get the best balance of expected return and risk.',
      why: 'Small errors in the inputs can produce extreme portfolios, so robust methods matter in practice.' },
];

// Default explanation per topic, used when no specific term is found.
export const TOPIC_WHY = {
    markets: 'Market news covers prices of stocks, bonds, currencies and commodities: what investors are buying and selling, and why.',
    economy: 'Economic news (growth, jobs, inflation, trade) shapes company profits and central-bank decisions over the coming months.',
    'central-banks': 'Central banks set interest rates, the price of money, which affects loans, savings, currencies and asset prices.',
    policy: 'Government decisions (taxes, budgets, trade rules, sanctions, elections, conflicts) can change costs and risks for whole industries.',
    companies: 'Company news (results, deals, job cuts) can move that company’s shares and sometimes its whole sector.',
};

const DISCLAIMER = 'Educational context, not financial advice.';

let compiled = null;
function compile() {
    if (compiled) return compiled;
    compiled = TERMS.map((t) => ({
        term: t,
        re: new RegExp(
            `(?<![\\p{L}\\p{N}])(${t.patterns
                .map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
                .sort((a, b) => b.length - a.length)
                .concat(t.regex || [])
                .join('|')})(?![\\p{L}\\p{N}])`,
            'giu',
        ),
    }));
    return compiled;
}

/** Terms found in a text, most specific first (by order of first appearance). */
export function findTerms(text, max = 3) {
    if (!text) return [];
    const hits = [];
    for (const { term, re } of compile()) {
        re.lastIndex = 0;
        const m = re.exec(text);
        if (m) hits.push({ term, index: m.index });
    }
    return hits
        .sort((a, b) => a.index - b.index)
        .slice(0, max)
        .map((h) => h.term);
}

/** Wrap glossary terms in already-escaped HTML with a tooltip span (first hit of each term only). */
export function annotate(escapedHtml) {
    if (!escapedHtml) return escapedHtml;
    let out = escapedHtml;
    const used = new Set();
    for (const { term, re } of compile()) {
        if (used.has(term.id)) continue;
        re.lastIndex = 0;
        let done = false;
        out = out.replace(re, (match) => {
            if (done) return match;
            done = true;
            used.add(term.id);
            const tx = glossaryText(term.id) || term;
            const tip = `${tx.label}: ${tx.what}`.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
            return `<span class="gl-term" tabindex="0" data-tip="${tip}">${match}</span>`;
        });
    }
    return out;
}

/** Plain-language explanation for a story: matched terms + the topic's default. */
export function explain({ title, summary, category_slug }) {
    const terms = findTerms(`${title || ''} ${summary || ''}`, 2).map((term) => ({ ...term, ...(glossaryText(term.id) || {}) }));
    const topicKey = `topic.${category_slug}`;
    const topic = t(topicKey);
    return {
        terms,
        topic: topic !== topicKey ? topic : TOPIC_WHY[category_slug] || null,
        disclaimer: t('disclaimer') || DISCLAIMER,
    };
}
